import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trophy, Crown, Sparkles, Search, X, Loader2, Save } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { emojiRain, playApplause } from './party';

/**
 * WinnerPickerTab — zero-friction admin flow for picking winners on the night.
 *
 *   [ Award category (searchable dropdown) ]  [ Employee (searchable dropdown) ]
 *   [ Optional judge note                                                     ]
 *                                                              [ Mark Winner ]
 *
 * Below the picker: a live table of every winner already selected, grouped by
 * category, each with a "Remove" that flips winner=false (nomination row is
 * kept so history stays intact).
 */
export default function WinnerPickerTab({ api }) {
  const [awards, setAwards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [awardId, setAwardId] = useState('');
  const [empId, setEmpId] = useState('');
  const [judgeNote, setJudgeNote] = useState('');
  const [awardQ, setAwardQ] = useState('');
  const [empQ, setEmpQ] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catsRes, empsRes, winnersRes] = await Promise.all([
        api.get('/api/rr/categories'),
        api.get('/api/admin/employees?status=active&limit=500'),
        api.get('/api/rr/admin/winners'),
      ]);
      // /api/rr/categories returns { edition, categories: [{_id, name, department, eligible_employees[]}] }
      const cats = catsRes.data?.categories || [];
      const awardsList = cats.map(c => ({
        _id: c._id,
        name: c.name,
        department: c.department,
        eligible_employees: c.eligible_employees || [],
        department_restricted: c.department_restricted,
      }));
      setAwards(awardsList);
      setEmployees((empsRes.data?.employees || empsRes.data || []).map(e => ({
        _id: e._id,
        name: e.personal?.full_name || e.name || 'Unnamed',
        designation: e.employment?.designation || e.designation || '',
        department: e.employment?.department || e.department || '',
      })));
      setWinners(winnersRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load picker data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredAwards = useMemo(() => {
    const n = awardQ.trim().toLowerCase();
    if (!n) return awards;
    return awards.filter(a => `${a.name} ${a.department||''}`.toLowerCase().includes(n));
  }, [awards, awardQ]);

  const selectedAward = awards.find(a => a._id === awardId);
  const selectedEmp = employees.find(e => e._id === empId);

  // When an award is selected AND it is department-restricted, auto-filter the
  // Employee dropdown to eligible employees for that category. Falls back to
  // the full active employee list for open (Agency Wide) categories.
  const eligibleIds = useMemo(() => {
    if (!selectedAward) return null;
    if (selectedAward.department_restricted === false) return null;
    const ids = (selectedAward.eligible_employees || []).map(e => e._id);
    return ids.length > 0 ? new Set(ids) : null;
  }, [selectedAward]);

  const filteredEmployees = useMemo(() => {
    const n = empQ.trim().toLowerCase();
    let list = employees;
    if (eligibleIds) list = list.filter(e => eligibleIds.has(e._id));
    if (!n) return list;
    return list.filter(e => `${e.name} ${e.designation} ${e.department}`.toLowerCase().includes(n));
  }, [employees, empQ, eligibleIds]);
  // Winners are grouped by award for the display table below
  const groupedWinners = useMemo(() => {
    const m = new Map();
    for (const w of winners) {
      const key = w.award_id;
      if (!m.has(key)) m.set(key, { award_name: w.award_name, award_department: w.award_department, rows: [] });
      m.get(key).rows.push(w);
    }
    return Array.from(m.values()).sort((a,b) => (a.award_name||'').localeCompare(b.award_name||''));
  }, [winners]);

  const pick = async () => {
    if (!awardId) { toast.error('Pick a category first'); return; }
    if (!empId)   { toast.error('Pick an employee'); return; }
    setSaving(true);
    try {
      await api.post(`/api/rr/admin/awards/${awardId}/pick-winner`, { employee_id: empId, judge_note: judgeNote });
      toast.success(`🏆 ${selectedEmp?.name} marked as winner!`);
      try { emojiRain(['🏆','✨','🎉','🌟'], 40); playApplause(); } catch {/* noop */}
      setEmpId(''); setEmpQ(''); setJudgeNote('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark winner');
    } finally { setSaving(false); }
  };

  const remove = async (awardId, employeeId, name) => {
    if (!window.confirm(`Remove ${name} as a winner? Their nomination history stays intact.`)) return;
    try {
      await api.delete(`/api/rr/admin/awards/${awardId}/winners/${employeeId}`);
      toast.success('Winner unmarked');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-fuchsia-500" /></div>;

  return (
    <div data-testid="rr-winner-picker">
      <Card className="p-6 mb-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-yellow-50">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-amber-600" />
          <h2 className="text-base font-bold text-neutral-900">Pick a winner</h2>
          <span className="ml-auto text-[11px] text-neutral-500">Any winner you pick shows up instantly on employee dashboards after you publish.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchableSelect
            label="Award category"
            query={awardQ}
            setQuery={setAwardQ}
            options={filteredAwards}
            valueKey="_id"
            renderLabel={a => a.name}
            renderSub={a => a.department}
            selectedId={awardId}
            selectedLabel={selectedAward ? `${selectedAward.name} · ${selectedAward.department||'Agency Wide'}` : ''}
            onSelect={id => { setAwardId(id); setAwardQ(''); }}
            testid="rr-picker-award"
          />
          <SearchableSelect
            label="Employee"
            query={empQ}
            setQuery={setEmpQ}
            options={filteredEmployees}
            valueKey="_id"
            renderLabel={e => e.name}
            renderSub={e => `${e.designation || ''}${e.department ? ' · ' + e.department : ''}`}
            selectedId={empId}
            selectedLabel={selectedEmp ? `${selectedEmp.name} · ${selectedEmp.designation || ''}` : ''}
            onSelect={id => { setEmpId(id); setEmpQ(''); }}
            testid="rr-picker-employee"
          />
        </div>
        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">Judge note (optional — shown on winners page)</label>
          <Textarea rows={2} value={judgeNote} onChange={(e) => setJudgeNote(e.target.value)} placeholder="e.g. For the countless late nights that made this year unforgettable." data-testid="rr-picker-note" />
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={pick} disabled={saving || !awardId || !empId} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="rr-picker-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Crown className="w-4 h-4 mr-1.5" /> Mark Winner</>}
          </Button>
        </div>
      </Card>

      {groupedWinners.length === 0 ? (
        <Card className="p-12 text-center text-neutral-500" data-testid="rr-picker-empty">
          <Trophy className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
          <p className="text-sm">No winners marked yet — pick your first one above.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedWinners.map(g => (
            <Card key={g.rows[0].award_id} className="p-5" data-testid={`rr-picker-group-${g.rows[0].award_id}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-600 grid place-items-center">
                  <Trophy className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-800/70 font-semibold">
                    {g.award_department || 'Agency Wide'}
                  </p>
                  <h3 className="text-sm font-bold text-neutral-900 leading-tight">{g.award_name}</h3>
                </div>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                  {g.rows.length} winner{g.rows.length===1?'':'s'}
                </span>
              </div>
              <div className="space-y-2">
                {g.rows.map(w => (
                  <div key={w.nomination_id + w.employee_id} className="flex items-center gap-3 p-2 rounded-lg bg-neutral-50">
                    <WinnerAvatar name={w.name} photo_url={w.photo_url} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 truncate">{w.name}</p>
                      <p className="text-xs text-neutral-500 truncate">
                        {[w.designation, w.department].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => remove(w.award_id, w.employee_id, w.name)} className="text-red-600 hover:bg-red-50" data-testid={`rr-picker-remove-${w.employee_id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Reusable typeahead select --------------------------------------------
function SearchableSelect({ label, query, setQuery, options, valueKey, renderLabel, renderSub, selectedId, selectedLabel, onSelect, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onBlur={() => setTimeout(() => setOpen(false), 150)}>
      <label className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5 block">{label}</label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          value={open ? query : selectedLabel || query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={`Search ${label.toLowerCase()}…`}
          className="pl-10"
          data-testid={testid}
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-neutral-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {options.length === 0 ? (
            <div className="p-3 text-xs text-neutral-400">No matches.</div>
          ) : options.slice(0, 25).map(o => (
            <button
              type="button"
              key={o[valueKey]}
              onClick={() => { onSelect(o[valueKey]); setOpen(false); }}
              className={`w-full text-left p-3 hover:bg-neutral-50 border-b border-neutral-100 ${o[valueKey]===selectedId?'bg-amber-50':''}`}
              data-testid={`${testid}-opt-${o[valueKey]}`}
            >
              <div className="text-sm font-medium text-neutral-900">{renderLabel(o)}</div>
              {renderSub(o) && <div className="text-xs text-neutral-500">{renderSub(o)}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WinnerAvatar({ name, photo_url }) {
  const initials = (name || '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase();
  const hues = ['from-pink-400 to-fuchsia-500','from-amber-400 to-orange-500','from-emerald-400 to-teal-500','from-sky-400 to-indigo-500','from-purple-400 to-pink-500'];
  const gradient = hues[(name?.length || 0) % hues.length];
  if (photo_url) return <img src={photo_url} alt={name} className="w-9 h-9 rounded-full object-cover border-2 border-amber-300 flex-shrink-0" />;
  return <div className={`w-9 h-9 rounded-full grid place-items-center bg-gradient-to-br ${gradient} text-white font-bold text-xs border-2 border-white shadow-sm flex-shrink-0`}>{initials}</div>;
}
